import React, { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import logger from '@vacationtracker/shared/functions/logger'

import IntlMessages from '@vacationtracker/shared/components/utils/IntlMessages'
import CircularProgress from '../circular-progress'
import { DocsDrawerNavbar } from './DocsDrawerNavbar'
import { rightActionBarTracker } from '../../containers/RightActionBar/services/rightActionBarTracking'


interface HelpDeskArticleAttributes {
  slug: string
}

interface HelpDeskArticle {
  id: number
  attributes: HelpDeskArticleAttributes
}

interface HelpDeskApiResponse {
  data: HelpDeskArticle[]
}

interface DocsDrawerContentProps {
  onClose?: () => void
}

// Static route mapping - shared constant to avoid duplication
const STATIC_ROUTE_MAPPING: Record<string, string> = {
  // Dashboard pages
  '/app/dashboard': '/how-do-i-access-the-dashboard/',
  '/app/my-dashboard': '/how-do-i-access-the-dashboard/',

  // Leave management
  '/app/leaves/request-leave': '/how-do-i-request-a-leave/',
  '/app/request-leave': '/how-do-i-request-a-leave/',
  '/app/leaves/add-leave': '/how-do-i-add-a-leave-for-a-user/',
  '/app/leaves/request-toil': '/how-do-i-request-toil-time-off-in-lieucomp-time/',
  '/app/request-toil': '/how-do-i-request-toil-time-off-in-lieucomp-time/',

  // Calendar
  '/app/calendar': '/what-information-can-i-see-on-my-calendar-view/',
  '/app/calendar/sync': '/guide-how-do-i-sync-my-calendar-with-vacation-tracker/',
  '/app/calendar/sync/google': '/guide-how-do-i-sync-my-calendar-with-vacation-tracker/',
  '/app/calendar/sync/outlook': '/guide-how-do-i-sync-my-calendar-with-vacation-tracker/',
  '/app/calendar/sync/ical': '/adding-a-new-calendar-using-ical-connection/',

  // Wallchart
  '/app/wallchart': '/how-can-i-use-wallchart-to-view-upcoming-leaves/',

  // Settings - Locations
  '/app/settings/locations': '/how-do-i-create-a-location/',
  '/app/settings/locations/create': '/how-do-i-create-a-location/',
  '/app/settings/location/:locationId/leave-policies/:leaveTypeId': '/how-do-i-set-a-brought-forward-rollover-policy/',

  // Settings - Departments
  '/app/settings/departments': '/how-do-i-create-a-department-within-vacation-tracker/',
  '/app/settings/departments/create': '/how-do-i-create-a-department-within-vacation-tracker/',
  '/app/settings/departments/:id': '/how-do-i-edit-department-information/',

  // Settings - Leave Policies
  '/app/settings/leave-policies': '/how-do-i-create-and-configure-leave-policies/',

  // Settings - Leave Types
  '/app/settings/leave-types': '/how-can-i-create-and-configure-leave-types/',

  // Settings - General
  '/app/settings/general': '/how-do-i-change-the-company-name-on-the-online-dashboard/',

  // Settings - Personal
  '/app/settings/personal': '/how-do-i-choose-my-view-preferences/',

  // Settings - Billing
  '/app/settings/billing': '/how-do-i-access-my-customer-portal-billing-information/',

  // Users
  '/app/users': '/how-do-i-add-import-users/',
  '/app/users/import': '/how-do-i-add-import-users/',

  // Reports
  '/app/reports': '/what-types-of-reports-can-i-export/',
  '/app/reports/leave-history-report': '/how-do-i-export-a-leave-request-report-leave-history/',
  '/app/reports/leave-balance-report': '/how-do-i-export-employee-leave-balances/',
  '/app/reports/company-logs': '/how-can-i-view-company-logs/',
  '/app/reports/scheduled-reports': '/guide-how-do-i-set-scheduled-reports/',

  // Notifications
  '/app/notifications': '/guide-notifications-within-vacation-tracker/',

  // Holidays
  '/app/holidays': '/how-do-i-set-holidays/',

  // Add-ons
  '/app/add-ons': '/how-do-i-set-a-probation-period-add-on/',

  // Integrations
  '/app/integrations/slack': '/slack-troubleshooting-slack-workspace-permissions/',
  '/app/integrations/microsoft-teams': '/microsoft-teams-how-do-i-add-the-vacation-tracker-bot-to-microsoft-teams/',
  '/app/integrations/google': '/google-workspace-editing-permissions/',

  // Import
  '/app/imports': '/data-import-importing-leave-data-using-our-template/',
  '/app/import/quotas': '/how-can-i-import-my-leave-data-into-vacation-tracker-leave-data-import/',
  '/app/import/toils': '/how-do-import-time-off-in-lieu-balances/',
  '/app/import/leaves': '/data-import-importing-leave-data-using-our-template/',
  '/app/import/details': '/how-do-i-import-users-profile-data/',
}

// Browser storage cache configuration
const CACHE_DURATION = 2 * 60 * 60 * 1000 // 2 hours (help articles change infrequently)
const CACHE_KEY = 'vt_helpdesk_articles'
const CACHE_TIMESTAMP_KEY = 'vt_helpdesk_articles_timestamp'

// Browser storage cache utilities
const getBrowserCache = (): { articles: HelpDeskArticle[] | null; timestamp: number | null } => {
  try {
    const articlesJson = localStorage.getItem(CACHE_KEY)
    const timestampStr = localStorage.getItem(CACHE_TIMESTAMP_KEY)

    if (!articlesJson || !timestampStr) {
      return { articles: null, timestamp: null }
    }

    const articles = JSON.parse(articlesJson) as HelpDeskArticle[]
    const timestamp = parseInt(timestampStr, 10)

    // Validate cache age
    if (Date.now() - timestamp > CACHE_DURATION) {
      // Cache expired, clear it
      localStorage.removeItem(CACHE_KEY)
      localStorage.removeItem(CACHE_TIMESTAMP_KEY)
      return { articles: null, timestamp: null }
    }

    return { articles, timestamp }
  } catch (error) {
    // Clear corrupted cache
    localStorage.removeItem(CACHE_KEY)
    localStorage.removeItem(CACHE_TIMESTAMP_KEY)
    return { articles: null, timestamp: null }
  }
}

const setBrowserCache = (articles: HelpDeskArticle[]): void => {
  try {
    const timestamp = Date.now()
    localStorage.setItem(CACHE_KEY, JSON.stringify(articles))
    localStorage.setItem(CACHE_TIMESTAMP_KEY, timestamp.toString())
  } catch (error) {
    // If localStorage is full or blocked, fall back to sessionStorage
    try {
      const timestamp = Date.now()
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(articles))
      sessionStorage.setItem(CACHE_TIMESTAMP_KEY, timestamp.toString())
    } catch (sessionError) {
      logger.error('Error saving helpdesk cache to sessionStorage:', sessionError)
    }
  }
}

// Cache management utilities
const clearBrowserCache = (): void => {
  try {
    localStorage.removeItem(CACHE_KEY)
    localStorage.removeItem(CACHE_TIMESTAMP_KEY)
    sessionStorage.removeItem(CACHE_KEY)
    sessionStorage.removeItem(CACHE_TIMESTAMP_KEY)
    memoryCache = { articles: null, timestamp: null }
  } catch (error) {
    logger.error('Error clearing helpdesk cache:', error)
  }
}

// Expose cache management for debugging (only in development)
if (process.env.NODE_ENV === 'development') {
  (window as unknown as Record<string, unknown>).clearHelpdeskCache = clearBrowserCache
  ;(window as unknown as Record<string, unknown>).getHelpdeskCache = getBrowserCache
}

// In-memory cache as final fallback (for cases where browser storage is disabled)
let memoryCache: { articles: HelpDeskArticle[] | null; timestamp: number | null } = {
  articles: null,
  timestamp: null,
}

// Fetch helpdesk articles from CMS API with multi-tier caching
const fetchHelpdeskArticles = async (): Promise<HelpDeskArticle[]> => {
  // Check browser cache first (persistent across sessions)
  const browserCache = getBrowserCache()
  if (browserCache.articles && browserCache.timestamp) {
    // Update memory cache for faster subsequent access
    memoryCache = browserCache
    return browserCache.articles
  }

  // Check memory cache (fastest, but lost on page refresh)
  if (memoryCache.articles && memoryCache.timestamp && Date.now() - memoryCache.timestamp < CACHE_DURATION) {
    return memoryCache.articles
  }

  try {
    // Fetch only published articles with minimal fields to optimize API response
    // Use large pageSize to get all articles in one request (avoiding pagination complexity)
    const apiUrl = 'https://cms.vacationtracker.io/api/help-desk-articles' +
      '?locale=en&publicationState=live&fields[0]=slug&pagination[pageSize]=1000'
    const response = await fetch(apiUrl)

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data: HelpDeskApiResponse = await response.json()

    // Update all cache layers
    setBrowserCache(data.data)
    memoryCache = { articles: data.data, timestamp: Date.now() }


    return data.data
  } catch (error) {
    logger.error('Failed to fetch helpdesk articles:', error)

    // Try to return stale cache if available (better than nothing)
    if (browserCache.articles) {
      logger.warning('🔄 Using stale browser cache as fallback')
      return browserCache.articles
    }
    if (memoryCache.articles) {
      logger.warning('🔄 Using stale memory cache as fallback')
      return memoryCache.articles
    }

    return []
  }
}

// Create dynamic route mapping based on helpdesk articles
const createRouteToHelpMapping = (articles: HelpDeskArticle[]): Record<string, string> => {
  const mapping: Record<string, string> = {}


  // Create a lookup map from article slugs for easy matching
  const articlesBySlug = new Map<string, string>()
  articles.forEach(article => {
    articlesBySlug.set(article.attributes.slug, `/${article.attributes.slug}/`)
  })


  // Use shared static mapping constant

  // First, add all static mappings as baseline
  Object.assign(mapping, STATIC_ROUTE_MAPPING)

  // Then, verify static mappings exist in API and update with dynamic data
  // This ensures we always have the latest article URLs from CMS
  Object.entries(STATIC_ROUTE_MAPPING).forEach(([route, helpPath]) => {
    const slug = helpPath.replace(/^\//, '').replace(/\/$/, '') // Remove leading/trailing slashes
    if (articlesBySlug.has(slug)) {
      const articlePath = articlesBySlug.get(slug)
      if (articlePath) {
        mapping[route] = articlePath
      }
    }
  })


  return mapping
}

export const DocsDrawerContent: React.FC<DocsDrawerContentProps> = ({ onClose }) => {
  const location = useLocation()
  const [currentUrl, setCurrentUrl] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isNavigatingFromMessage, setIsNavigatingFromMessage] = useState<boolean>(false)

  // Get contextual help URL based on current page (async)
  useEffect(() => {
    const loadContextualUrl = async () => {
      setIsLoading(true)
      try {
        const articles = await fetchHelpdeskArticles()
        const routeToHelpMapping = articles.length > 0
          ? createRouteToHelpMapping(articles)
          : STATIC_ROUTE_MAPPING

        const helpdesk = 'https://vacationtracker.io/helpdesk'
        const embed = '?embed=app'
        let url = `${helpdesk}/${embed}` // default fallback

        if (routeToHelpMapping[location.pathname]) {
          url = `${helpdesk}${routeToHelpMapping[location.pathname]}${embed}`
        } else {
          // Pattern matching logic (simplified version)
          for (const [route, helpPath] of Object.entries(routeToHelpMapping)) {
            if (route.includes('/:') && location.pathname.match(/\/[a-f0-9-]{36}$|\/\d+$/)) {
              const baseRoute = route.replace(/\/:\w+.*$/, '')
              if (location.pathname.startsWith(baseRoute)) {
                url = `${helpdesk}${helpPath}${embed}`
                break
              }
            }
          }
        }

        setCurrentUrl(url)

        // Track docs opened with page and article info
        const urlWithoutEmbed = url.replace('?embed=app', '').replace('&embed=app', '')
        const articleMatch = urlWithoutEmbed.match(/\/([^/]+)\/$/)
        const articleSlug = articleMatch ? articleMatch[1] : undefined
        const articleTitle = Object.entries(routeToHelpMapping).find(
          ([, helpPath]) => url.includes(helpPath.replace(/^\//, '').replace(/\/$/, ''))
        )?.[0]

        rightActionBarTracker.trackDocsOpened({
          pageUrl: urlWithoutEmbed,
          articleId: articleSlug,
          articleTitle: articleTitle || location.pathname,
          appPageUrl: location.pathname,
        })
      } catch (error) {
        logger.error('Error loading contextual help URL:', error)
        // Fallback URL
        setCurrentUrl('https://vacationtracker.io/helpdesk/?embed=app')
      } finally {
        setIsLoading(false)
      }
    }

    loadContextualUrl()
  }, [location.pathname])

  // Update iframe when URL changes (but not when change comes from postMessage)
  useEffect(() => {
    if (!currentUrl || isLoading || isNavigatingFromMessage) return

    // Update iframe if it exists
    const iframe = document.querySelector('.docs-drawer-content iframe') as HTMLIFrameElement
    if (iframe && iframe.src !== currentUrl) {
      iframe.src = currentUrl
    }
  }, [currentUrl, isLoading, isNavigatingFromMessage])

  // Listen for iframe navigation changes via postMessage
  // This is used to update the iframe when the user navigates within the helpdesk content
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Only accept messages from our helpdesk domain
      if (event.origin !== 'https://vacationtracker.io') return

      if (event.data && event.data.type === 'navigation' && event.data.url) {
        // Update currentUrl to reflect the actual page being viewed in iframe
        const newPath = event.data.url
        // Construct full URL with helpdesk base and embed parameter
        const helpdesk = 'https://vacationtracker.io'
        const fullUrl = `${helpdesk}${newPath}?embed=app`

        // Set flag to prevent iframe src update loop
        setIsNavigatingFromMessage(true)
        setCurrentUrl(fullUrl)

        // Reset flag after a brief delay
        setTimeout(() => {
          setIsNavigatingFromMessage(false)
        }, 100)
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  const handleHomeNavigation = () => {
    // Track the home navigation
    rightActionBarTracker.trackDocsHomeClicked({
      currentPageUrl: currentUrl,
    })

    // Navigate to helpdesk root
    const homeUrl = 'https://vacationtracker.io/helpdesk/?embed=app'
    setCurrentUrl(homeUrl)
  }

  const handleOpenInNewTab = () => {
    // Extract article info from URL
    const urlWithoutEmbed = currentUrl.replace('?embed=app', '').replace('&embed=app', '')
    const articleMatch = urlWithoutEmbed.match(/\/([^/]+)\/$/)
    const articleId = articleMatch ? articleMatch[1] : undefined

    // Track the open in new tab action
    rightActionBarTracker.trackDocsOpenInNewTabClicked({
      pageUrl: urlWithoutEmbed,
      articleId,
    })

    // Remove embed parameter and open in new tab
    window.open(urlWithoutEmbed, '_blank', 'noopener,noreferrer')
  }

  const handleClose = () => {
    if (onClose) {
      onClose()
    }
  }

  return (
    <div className="docs-drawer-content docs-drawer-force-light-theme" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Navbar Header */}
      <DocsDrawerNavbar
        isLoading={isLoading}
        showHomeButton={true}
        showOpenInNewTab={true}
        homeTooltip={<IntlMessages id="app.goToHelpdeskHome" />}
        onHomeNavigation={handleHomeNavigation}
        onOpenInNewTab={handleOpenInNewTab}
        onClose={handleClose}
        disableOpenInNewTab={!currentUrl}
        forceLightTheme={true}
      />

      {/* Iframe Content */}
      {isLoading ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          color: '#666',
        }}>
          <CircularProgress />
        </div>
      ) : (
        <iframe
          src={currentUrl}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            flex: 1,
          }}
          title={(<IntlMessages id="app.documentation" />) as unknown as string}
          loading="lazy"
          sandbox="allow-same-origin allow-scripts"
        />
      )}
    </div>
  )
}